<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
  <header>
    <div class="flex gap-[2rem]">
      <h1>{{ asset?.assetId || 'New Asset' }}</h1>
    </div>
    <div class="buttons">
      <p-button [label]="'Save' | translate" (click)="onSave()" [disabled]="isButtonDisabled()" />
      <p-button [label]="'Cancel' | translate" (click)="onClose()" [outlined]="true" />
    </div>
  </header>

  @if (asset) {
    <div class="content">
      <p-panel [header]="'Asset_Details.GeneralInformation' | translate" [toggleable]="true" styleClass="mb-4">
        <p-card>
          <form [formGroup]="formAssetGroup">
            <div class="grid-12 gap-[2rem]">
              <div class="span-6">
                <!-- Asset ID -->
                @if (isEditing) {
                  <div class="form-box">
                    <label for="assetId">
                      {{ 'Asset_Details.Asset_Id' | translate }}
                    </label>
                    <p>{{ asset.assetId }}</p>
                  </div>
                }
                <!-- Asset Prefix -->
                <div class="form-box">
                  <label for="">
                    {{ 'Asset_Details.Asset_Prefix' | translate }}
                  </label>
                  @if (prefix.value; as selectedPrefix) {
                    <p>
                      <strong>{{ selectedPrefix.prefix }}</strong> -
                      {{ selectedPrefix.description }}
                    </p>
                  } @else {
                    <p class="text-muted">No prefix assigned</p>
                  }
                </div>
                <!-- Asset Description -->
                <div class="form-box">
                  <label for="">{{ 'Asset_Details.Asset_Description' | translate }}</label>
                  <textarea pTextarea rows="5" cols="30" id="description" formControlName="description" class="w-full"></textarea>
                </div>
                <!-- Category -->
                <div class="form-box">
                  <label for="assetCategory" class="required">
                    {{ 'Asset_Details.Category' | translate }}
                  </label>
                  <p-autocomplete
                    fluid
                    formControlName="assetCategory"
                    optionLabel="name"
                    [dropdown]="true"
                    [suggestions]="filteredCategories"
                    (completeMethod)="categoryTypeahead($event)"
                    class="w-full"
                  />
                </div>
                <!-- Type -->
                <div class="form-box">
                  <label for="assetType" class="required">
                    {{ 'Asset_Details.Type' | translate }}
                  </label>
                  @if (!assetCategory.value) {
                    <p class="cursor-not-allowed">
                      {{ 'Asset_Details.Addtypefirst' | translate }}
                    </p>
                  }
                  @if (assetCategory.value?.id) {
                    <p-autocomplete
                      fluid
                      formControlName="assetType"
                      optionLabel="name"
                      [dropdown]="true"
                      [showClear]="true"
                      [suggestions]="filteredTypes"
                      (completeMethod)="typeTypeahead($event)"
                      class="w-full"
                    />
                    @if (!assetType.value?.id) {
                      <div class="mt-2">
                        <p-button [label]="'Modal_Type.Add' | translate" icon="pi pi-plus" variant="outlined" (click)="onAddNewType()" />
                      </div>
                    }
                  }
                </div>
                <!-- Model -->
                @if (assetType.value?.id) {
                  <div class="form-box">
                    <label for="assetModelReference">
                      {{ 'Asset_Details.Model' | translate }}
                    </label>
                    @if (filteredModels.length > 0) {
                      <p-autocomplete
                        fluid
                        formControlName="assetModel"
                        optionLabel="name"
                        [dropdown]="true"
                        [showClear]="true"
                        [suggestions]="filterModels"
                        (completeMethod)="modelTypeahead($event)"
                        class="w-full"
                      />
                    }
                    @if (!assetModel.value?.id) {
                      <div class="mt-2">
                        <p-button [label]="'Modal_Model.Add' | translate" icon="pi pi-plus" variant="outlined" (click)="onAddNewModel()" />
                      </div>
                    }
                  </div>
                } @else if (assetCategory.value?.id) {
                  <div class="form-box">
                    <label for="assetModelReference">
                      {{ 'Asset_Details.Model' | translate }}
                    </label>
                    <p class="cursor-not-allowed">
                      {{ 'Asset_Details.Addtypefirst' | translate }}
                    </p>
                  </div>
                }
              </div>
            </div>
          </form>
        </p-card>
      </p-panel>
      <!-- Location -->
      <p-panel [header]="'Asset_Details.Location' | translate" [toggleable]="true" styleClass="mb-4">
        <p-card>
          <div class="grid-12 gap-[2rem]">
            <div class="span-6">
              <div class="form-box">
                <label for="siteCode" class="required">
                  {{ 'Asset_Details.SiteCode' | translate }}
                </label>
                <p-select
                  id="siteCode"
                  [options]="sites"
                  [formControl]="siteReference"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select a site"
                  [filter]="false"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </p-card>
      </p-panel>
      <!-- Knitting Machine Details -->
      @if (showKnittingMachineDetails) {
        <p-panel [header]="'Knitting_Machine_Details.Title' | translate" [toggleable]="true" styleClass="mb-4">
          <p-card>
            <form [formGroup]="knittingMachineDetails">
              <div class="grid-12 gap-[1rem]">
                <div class="span-3">
                  <div class="form-box">
                    <label for="diameterInches">
                      {{ 'Knitting_Machine_Details.Diameter' | translate }}
                    </label>
                    <p-inputNumber id="diameterInches" formControlName="diameterInches" class="w-full" />
                  </div>
                </div>
                <div class="span-3">
                  <div class="form-box">
                    <label for="gauge">
                      {{ 'Knitting_Machine_Details.Gauge' | translate }}
                    </label>
                    <p-inputNumber id="gauge" formControlName="gauge" class="w-full" />
                  </div>
                </div>
                <div class="span-3">
                  <div class="form-box">
                    <label for="feeders">
                      {{ 'Knitting_Machine_Details.Feeders' | translate }}
                    </label>
                    <p-inputNumber id="feeders" formControlName="feeders" class="w-full" />
                  </div>
                </div>
                <div class="span-3">
                  <div class="form-box">
                    <label for="cylinderNeedles">
                      {{ 'Knitting_Machine_Details.Needles' | translate }}
                    </label>
                    <p-inputNumber id="cylinderNeedles" formControlName="cylinderNeedles" class="w-full" />
                  </div>
                </div>
                @if (isDoubleKnit) {
                  <div class="span-3">
                    <div class="form-box">
                      <label for="dialNeedles">Dial Needles</label>
                      <p-inputNumber id="dialNeedles" formControlName="dialNeedles" class="w-full" />
                    </div>
                  </div>
                }
              </div>
            </form>
          </p-card>
        </p-panel>
      }
      @if (showDyeingMachineDetails) {
        <p-panel [header]="'Dyeing_Machine_Details.Title' | translate" [toggleable]="true" styleClass="mb-4">
          <p-card>
            <form [formGroup]="dyeingMachineDetails">
              <div class="form-box">
                <label for="NoOfPorts">
                  {{ 'Dyeing_Machine_Details.NumberofPorts' | translate }}
                </label>
                <p-inputnumber inputId="NoOfPorts" formControlName="numberOfPorts" />
              </div>
              <div class="form-box">
                <label for="PortCapacity">
                  {{ 'Dyeing_Machine_Details.PortCapacity' | translate }}
                </label>
                <p-inputnumber inputId="PortCapacity" formControlName="portCapacity" [minFractionDigits]="0" [maxFractionDigits]="2" />
              </div>
            </form>
          </p-card>
        </p-panel>
      }
      <!-- Asset Capacity -->
      @if (showCapacitySection) {
        <p-panel [header]="'Assets_Capacity.Title' | translate" [toggleable]="true" styleClass="mb-4">
          <p-card>
            <form [formGroup]="assetCapacity">
              <div>
                <div class="grid-12 gap-[2rem]">
                  <div class="span-6">
                    <div class="grid-12 gap-[1rem]">
                      <div class="span-4">
                        <div class="form-box">
                          <label for="maxCapacity">
                            {{ 'Assets_Capacity.MaxCapacity' | translate }}
                          </label>
                          <input pInputText type="number" id="maxCapacity" formControlName="maxCapacity" class="w-full" />
                        </div>
                      </div>
                      <div class="span-4">
                        <div class="form-box">
                          <label for="minCapacity">
                            {{ 'Assets_Capacity.MinCapacity' | translate }}
                          </label>
                          <input pInputText type="number" id="minCapacity" formControlName="minCapacity" class="w-full" />
                        </div>
                      </div>
                      <div class="span-4">
                        <div class="form-box">
                          <label for="capacityUOM">
                            {{ 'Assets_Capacity.CapacityUOM' | translate }}
                          </label>
                          <p-select
                            id="capacityUOM"
                            [options]="capacityUOMSelectOptions"
                            optionLabel="label"
                            optionValue="value"
                            formControlName="capacityUOM"
                            [filter]="false"
                            class="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </p-card>
        </p-panel>
      }
      <!-- Scanning Station Details -->
      @if (isScanningStation) {
        <p-panel header="Scanning Station Details" [toggleable]="true" styleClass="mb-4">
          <p-card>
            <div class="grid-12 gap-[2rem]">
              <div class="span-6">
                <div class="form-box">
                  <label for="processSearch" class="required"> Allowed Processes </label>
                  <p-autocomplete
                    id="processSearch"
                    [formControl]="processSearchQuery"
                    #processSearch="cslServerSearch"
                    [cslServerSearch]="fetchProcesses"
                    [suggestions]="processSearch.results()"
                    (onSelect)="addProcess($event)"
                    placeholder="Search for processes..."
                    class="w-full"
                    [dropdown]="false"
                    optionLabel="displayName"
                  >
                  </p-autocomplete>
                </div>
                @if (selectedProcesses.length > 0) {
                  <div class="mt-3">
                    <label class="block text-sm font-medium text-gray-700 mb-2"> Selected Processes ({{ selectedProcesses.length }}) </label>
                    <ul class="col-sm">
                      @for (process of selectedProcesses; track process.id) {
                        <li class="row-between-center bg-gray-100 rounded-md px-3 py-2">
                          <span>{{ localeHelperService.getLocalizedValue(process.name) }}</span>
                          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="danger" (click)="removeProcess(process)" />
                        </li>
                      }
                    </ul>
                  </div>
                } @else {
                  <p class="text-red-500 mt-2 text-sm">At least one process must be selected for scanning stations.</p>
                }
              </div>
            </div>
          </p-card>
        </p-panel>
      }
      <!-- Weight Station Details -->
      @if (isWeightStation) {
        <p-panel header="Weight Station Details" [toggleable]="true" styleClass="mb-4">
          <p-card>
            <form [formGroup]="weightStationDetails">
              <div class="grid-12 gap-[2rem]">
                <div class="span-6">
                  <div class="form-box">
                    <label for="weightStationArea">Area</label>
                    <input pInputText id="weightStationArea" formControlName="area" placeholder="e.g. Knitting" class="w-full" />
                    <small class="text-gray-500">Stations with the same area are grouped together on the Stations page.</small>
                  </div>
                </div>
                <div class="span-6">
                  <div class="form-box">
                    <label for="weightStationRoles">Accessible Roles</label>
                    <p-multiselect
                      inputId="weightStationRoles"
                      formControlName="accessibleRoles"
                      [options]="roles()"
                      optionLabel="name"
                      optionValue="name"
                      [filter]="true"
                      display="chip"
                      [selectionLimit]="maxWeightStationRoles"
                      [showToggleAll]="false"
                      placeholder="All station users"
                      class="w-full"
                    />
                    <small class="text-gray-500">Up to {{ maxWeightStationRoles }} roles. Leave empty to let every station user open it.</small>
                    @if (weightStationDetails.controls.accessibleRoles.hasError('maxlength')) {
                      <small class="text-red-500">Remove roles until there are {{ maxWeightStationRoles }} or fewer.</small>
                    }
                  </div>
                </div>
              </div>
            </form>
          </p-card>
        </p-panel>
      }
      <!-- Model Properties -->
      <p-panel [header]="'Model_Properties.ModelProperties' | translate" [toggleable]="true" styleClass="mb-4">
        <p-card>
          @if (filteredPropertyModels.length) {
            <ul class="col-sm">
              @for (property of filteredPropertyModels; track property.id) {
                <li
                  class="justify-between max-w-fit row-start-center font-medium px-2 py-1 rounded-full border-2"
                  [ngClass]="{
                    'bg-gray-300 text-gray-500 cursor-not-allowed': !property.setValuesAtAssetLevel,
                    'bg-white text-black cursor-pointer': property.setValuesAtAssetLevel,
                  }"
                  (click)="enableEditing(property)"
                >
                  <p>{{ property.name }}: {{ getValues(property.defaultValues) }}</p>
                </li>
              }
            </ul>
          } @else {
            <p class="cursor-not-allowed mt-3 text-red-500">
              {{ 'Model_Properties.NoPropertiesFound' | translate }}
            </p>
          }

          @if (editingProperty; as selectedProperty) {
            <div class="mt-2">
              <small class="block text-sm font-medium text-gray-700">
                {{
                  selectedProperty.multipleValues
                    ? ('Model_Properties.AddDefaultValues' | translate)
                    : ('
            Model_Properties.AddSingleDefaultValue' | translate)
                }}
              </small>
              <div class="row-sm mt-1">
                <input
                  pInputText
                  type="text"
                  [formControl]="newValue"
                  placeholder="Enter a value"
                  [hidden]="!selectedProperty.multipleValues && addValues.length >= 1"
                  (keydown.enter)="addValueToDefaultValues()"
                  (keydown.escape)="onCancelEdit()"
                />
                <p-button icon="pi pi-plus" (click)="addValueToDefaultValues()" [disabled]="!selectedProperty.multipleValues && addValues.length >= 1" />
              </div>

              @if (!selectedProperty.multipleValues && addValues.length >= 1) {
                <p class="text-red-500 mt-2">
                  {{ ' Model_Properties.PropertiesText' | translate }}
                </p>
              }

              <ul class="mt-2">
                @for (value of addValues; track $index; let i = $index) {
                  <li class="py-2 pl-4 row-between-center bg-gray-100 rounded-md mb-1 w-fit">
                    {{ value }}
                    <p-button icon="pi pi-times" class="mx-[1rem]" (click)="removeValueFromDefaultValues(i)" />
                  </li>
                }
              </ul>

              <div class="row-sm mt-2">
                <p-button label="Close" (click)="onCancelEdit()" />
              </div>
            </div>
          }
        </p-card>
      </p-panel>
    </div>
  }
</main>
